import React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { StaffFormValues } from "../StaffForm";

// Generate month options
const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

// Generate day options (1-31)
const DAYS = Array.from({ length: 31 }, (_, i) => ({
  value: (i + 1).toString().padStart(2, "0"),
  label: (i + 1).toString(),
}));

interface BirthdayPickerProps {
  form: UseFormReturn<StaffFormValues>;
}

const BirthdayPicker: React.FC<BirthdayPickerProps> = ({ form }) => {
  // Reference to keep track of current month/day selections
  const [month, setMonth] = React.useState<string | null>(null);
  const [day, setDay] = React.useState<string | null>(null);

  // Format the birthday for display
  const formatBirthday = (month: string, day: string) => {
    try {
      const monthIndex = parseInt(month, 10) - 1; // Convert to 0-indexed
      const monthName = new Date(2000, monthIndex).toLocaleString('default', { month: 'long' });
      return `${monthName} ${parseInt(day, 10)}`;
    } catch (e) {
      console.error("Error formatting birthday:", e);
      return null;
    }
  };

  // Parse the current birthday value when the component mounts
  React.useEffect(() => {
    // For Option 2A (VARCHAR MM-DD format)
    const currentValue = form.getValues("birthday");
    if (currentValue && typeof currentValue === 'string') {
      if (currentValue.includes('-')) {
        // Format is MM-DD
        const [monthVal, dayVal] = currentValue.split('-');
        setMonth(monthVal);
        setDay(dayVal);
      } else if (currentValue.length === 5) {
        // Format is MM/DD
        const monthVal = currentValue.substring(0, 2);
        const dayVal = currentValue.substring(3, 5);
        setMonth(monthVal);
        setDay(dayVal);
      }
    }
    
    // For Option 2B (separate month and day columns)
    try {
      const monthValue = form.getValues("birthday_month" as any);
      const dayValue = form.getValues("birthday_day" as any);
      
      if (monthValue) {
        const monthStr = typeof monthValue === 'number' 
          ? monthValue.toString().padStart(2, '0')
          : String(monthValue).padStart(2, '0');
        setMonth(monthStr);
      }
      
      if (dayValue) {
        const dayStr = typeof dayValue === 'number'
          ? dayValue.toString().padStart(2, '0')
          : String(dayValue).padStart(2, '0');
        setDay(dayStr);
      }
    } catch (e) {
      console.error("Error accessing birthday_month/day:", e);
    }
  }, [form]);

  // Update the birthday field when month or day changes
  const updateBirthday = (newMonth: string | null, newDay: string | null) => {
    if (newMonth && newDay) {
      // Option 2A: Store as MM-DD format
      form.setValue("birthday", `${newMonth}-${newDay}`);
      
      // Option 2B: Store as separate columns
      try {
        form.setValue("birthday_month" as any, parseInt(newMonth, 10));
        form.setValue("birthday_day" as any, parseInt(newDay, 10));
      } catch (e) {
        console.error("Error setting birthday_month/day:", e);
      }
    } else {
      // Clear all fields if either month or day is missing
      form.setValue("birthday", null);
      try {
        form.setValue("birthday_month" as any, null);
        form.setValue("birthday_day" as any, null);
      } catch (e) {
        console.error("Error clearing birthday_month/day:", e);
      }
    }
  };

  // Handle month selection
  const handleMonthChange = (value: string) => {
    setMonth(value);
    updateBirthday(value, day);
  };

  // Handle day selection
  const handleDayChange = (value: string) => {
    setDay(value);
    updateBirthday(month, value);
  };

  return (
    <FormField
      control={form.control}
      name="birthday"
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>Birthday (month & day only)</FormLabel>
          <FormControl>
            <div className="relative">
              <div className="flex flex-row items-center gap-2">
                <Select value={month || ""} onValueChange={handleMonthChange}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month) => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={day || ""} onValueChange={handleDayChange}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Day" />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <CalendarIcon className="ml-2 h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </FormControl>
          <p className="text-xs text-muted-foreground mt-1">
            Only month and day are stored for HR compliance.
          </p>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default BirthdayPicker; 